import { VASTClient, VASTTracker } from '@dailymotion/vast-client';
import Logger from '../../utils/logger';

const LINEAR = 'linear';
const MIME_TYPE_MP4 = 'video/mp4';

export async function fetchAdFromVAST(xmlUrl) {
  const vastClient = new VASTClient();
  /*
   * How VAST XML is Parsed in get method:
   * 1. If the VAST XML contains Ad Pod (multiple ads with sequence numbers):
   *    - The result will be a list of Ads sorted by sequence number (ascending order).
   *    - Multiple ads with the same sequence number are grouped together.
   *    - Sequence="1" ads first, then "2", then "3", etc.
   * 2. If the VAST XML contains only Standalone Ads (no sequence attribute):
   *    - The result will return the first standalone ads.
   *    - Standalone ads are considered separately and the rest of Ads will be returned in remainingAds.
   * 3. If the VAST XML contains both Ad Pods and Standalone Ads:
   *    - The result will be Ad Pod ads (with sequence) as it take priority.
   *    - Standalone ads are considered separately and will be returned in remainingAds.
   */
  const ads = await vastClient.get(xmlUrl, {
    allowMultipleAds: true, // Enables fetching multiple ads from the VAST XML (i.e., supports Ad Pods).
    followAdditionalWrappers: true, // Ensures that any wrapper VAST URLs are followed and fully resolved.
  })
    .then(parsedVAST => parsedVAST?.ads) // Extracts the parsed ads from the VAST response. Ads are already sorted
    .catch((err) => {
      Logger.warn('fetchAdFromVAST error', err);
      throw err;
    });

  // append standalone ads to the end of the list of ads
  const hasRemainingAds = vastClient.hasRemainingAds();
  if (hasRemainingAds) {
    const remainingAds = await vastClient.getNextAds(true)
      .then(parsedVAST => parsedVAST?.ads);
    ads.push(...remainingAds);
  }

  return ads;
}

/**
 * Get the first valid creative from an ad
 */
export const getCreative = (ad, type) => {
  if (!ad.creatives) {
    return null;
  }

  return ad.creatives.find(creative => creative.mediaFiles && creative.type === type) || null;
};

/**
 * Get the URL of the first media file in the creative
 */

export const getMediaFileUrl = (creative) => {
  const mediaFiles = creative.mediaFiles;
  if (!mediaFiles.length) {
    return null;
  }

  return mediaFiles.find(mediaFile => mediaFile?.fileURL)?.fileURL || null;
};

export function getValidAds(rawAds, onInvalidAd = () => {}) {
  const ads = [];

  rawAds.forEach((ad) => {
    const creative = getCreative(ad, LINEAR);
    const mediaFileUrl = getMediaFileUrl(creative);
    if (mediaFileUrl) {
      ad.getLinear = () => creative;
      ads.push(ad);
    } else {
      onInvalidAd(ad);
    }
  });

  return ads;
}

export function trackEvents(vastTracker, event, errorCode, ad) {
  let tracker = vastTracker;
  if (!tracker && ad) {
    const creative = getCreative(ad, LINEAR);
    tracker = new VASTTracker(null, ad, creative);
  }
  if (!tracker) {
    return;
  }
  if (event === 'error') {
    tracker.errorWithCode(errorCode);
  } else {
    tracker.track(event);
  }
}

export function getBestMedia(ad, target) {
  let best = Number.POSITIVE_INFINITY;
  let besti = -1;
  ad.mediaFiles.forEach((media, i) => {
    if (media.mimeType === MIME_TYPE_MP4) {
      // Root of the sum of the squares seems as good a mesure as any for a
      // two-dimensional distance. Pythagoras FTW!
      const distance = Math.sqrt(
        ((target.width - media.width) ** 2) + ((target.height - media.height) ** 2),
      );

      if (distance < best) {
        best = distance;
        besti = i;
      } else if (distance === best) {
        // If the two files are equally close to the target resolution, use
        // bitrate as the pivot. Has bitrate > closer to target bitrate > highest
        // bitrate
        const other = ad.mediaFiles[besti];
        const otherBR = other.bitrate || other.maxBitrate;
        const mediaBR = media.bitrate || media.maxBitrate;

        if (mediaBR && !otherBR) {
          besti = i;
        } else if (target.bitrate && otherBR && mediaBR) {
          if (Math.abs(mediaBR - target.bitrate) < Math.abs(otherBR - target.bitrate)) {
            besti = i;
          }
        } else if (mediaBR > otherBR) {
          besti = i;
        }
      }
    }
  });
  if (besti === -1) {
    return null;
  }

  return ad.mediaFiles[besti];
}
